import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Shield, AlertTriangle, FileText, Clock, CheckCircle2, XCircle,
  Plus, ChevronDown, ChevronUp, Loader2, Eye, Save, X, Calendar,
  Archive, RefreshCw, Database, Lock
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-AU', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtDate = (dt) => dt ? new Date(dt + 'T00:00:00').toLocaleDateString('en-AU', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const statusColor = {
  assessing:     'bg-yellow-50 text-yellow-700 border-yellow-200',
  notifiable:    'bg-red-50 text-red-700 border-red-200',
  not_notifiable:'bg-green-50 text-green-700 border-green-200',
  closed:        'bg-slate-50 text-slate-500 border-slate-200',
};

// ── Tab button ────────────────────────────────────────────────────────────────
function Tab({ active, onClick, icon: Icon, label, count }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
        active ? 'bg-white shadow-sm text-primary-700 border border-slate-200' : 'text-slate-500 hover:text-slate-700'
      }`}>
      <Icon size={15} />
      {label}
      {count !== undefined && (
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${count > 0 ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── Access Log tab ────────────────────────────────────────────────────────────
function AccessLogTab() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState({ resource_type: '', action: '' });

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filter.resource_type) q.set('resource_type', filter.resource_type);
    if (filter.action) q.set('action', filter.action);
    api.get(`/compliance/access-log?${q.toString()}`)
      .then(r => setRows(r.data || []))
      .catch(() => toast.error('Failed to load access log'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <select value={filter.resource_type} onChange={e => setFilter(f => ({ ...f, resource_type: e.target.value }))}
          className="input-field !w-auto text-sm !py-2">
          <option value="">All resource types</option>
          {['client','staff','document','shift','incident','medication','progress_note'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select value={filter.action} onChange={e => setFilter(f => ({ ...f, action: e.target.value }))}
          className="input-field !w-auto text-sm !py-2">
          <option value="">All actions</option>
          {['view','create','update','delete','export','login','logout'].map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <button onClick={load} className="btn-secondary btn-sm gap-1.5">
          <RefreshCw size={13} /> Refresh
        </button>
        <p className="text-xs text-slate-400 ml-auto">{rows.length} entries shown (latest 200)</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-primary-500" /></div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-slate-400 text-sm">No access log entries found</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Resource</th>
                <th className="px-4 py-3 text-left">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-700 text-xs">{r.user_email || '—'}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.user_role || '—'}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      r.action === 'delete' ? 'bg-red-50 text-red-700' :
                      r.action === 'create' ? 'bg-green-50 text-green-700' :
                      r.action === 'view'   ? 'bg-blue-50 text-blue-700' :
                      'bg-slate-50 text-slate-600'}`}>
                      {r.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{r.resource_type}{r.resource_id ? ` · ${r.resource_id.slice(0,8)}…` : ''}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">{r.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Breach Register tab ───────────────────────────────────────────────────────
function BreachRegisterTab() {
  const [breaches, setBreaches]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [expanded, setExpanded]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({
    title: '', description: '', discovered_at: new Date().toISOString().slice(0,16),
    individuals_affected: 0, data_types_affected: [], status: 'assessing',
    remediation_steps: '', oaic_notified: false, individuals_notified: false,
  });

  const DATA_TYPES = ['Names','Contact details','Health information','Financial information','TFN','Passwords','Location data','Government identifiers'];

  const load = () => {
    setLoading(true);
    api.get('/compliance/breach-register')
      .then(r => setBreaches(r.data || []))
      .catch(() => toast.error('Failed to load breach register'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error('Title and description are required');
    setSaving(true);
    try {
      await api.post('/compliance/breach-register', form);
      toast.success('Breach logged');
      setShowForm(false);
      setForm({ title:'', description:'', discovered_at: new Date().toISOString().slice(0,16), individuals_affected:0, data_types_affected:[], status:'assessing', remediation_steps:'', oaic_notified:false, individuals_notified:false });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, updates) => {
    try {
      await api.put(`/compliance/breach-register/${id}`, updates);
      toast.success('Updated');
      load();
    } catch { toast.error('Failed'); }
  };

  const toggleDataType = (type) => {
    setForm(f => ({
      ...f,
      data_types_affected: f.data_types_affected.includes(type)
        ? f.data_types_affected.filter(t => t !== type)
        : [...f.data_types_affected, type],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          All data breaches must be assessed within <strong>30 days</strong> under the NDB scheme (Privacy Act s.26WH).
          If eligible, notify the OAIC and affected individuals as soon as practicable.
        </p>
        <button onClick={() => setShowForm(s => !s)} className="btn-primary btn-sm gap-1.5 shrink-0">
          <Plus size={13} /> Log Breach
        </button>
      </div>

      {/* New breach form */}
      {showForm && (
        <div className="card border-red-200 p-5 space-y-4 bg-red-50/30">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Log New Data Breach / Incident</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400 hover:text-slate-600" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="input-label">Title *</label>
              <input className="input-field" placeholder="e.g. Unauthorised access to client records" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className="input-label">Description *</label>
              <textarea className="input-field min-h-[80px]" placeholder="Describe what happened, what data was affected, and how it was discovered…"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="input-label">Date/Time Discovered *</label>
              <input type="datetime-local" className="input-field" value={form.discovered_at} onChange={e => setForm(f => ({ ...f, discovered_at: e.target.value }))} />
            </div>
            <div>
              <label className="input-label">Individuals Affected (estimated)</label>
              <input type="number" min="0" className="input-field" value={form.individuals_affected} onChange={e => setForm(f => ({ ...f, individuals_affected: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="md:col-span-2">
              <label className="input-label">Data Types Affected</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DATA_TYPES.map(t => (
                  <button key={t} type="button" onClick={() => toggleDataType(t)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form.data_types_affected.includes(t)
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="input-label">Remediation Steps</label>
              <textarea className="input-field min-h-[60px]" placeholder="What steps are being taken to contain and remediate…"
                value={form.remediation_steps} onChange={e => setForm(f => ({ ...f, remediation_steps: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-red-100">
            <button onClick={() => setShowForm(false)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={submit} disabled={saving} className="btn-sm bg-red-600 text-white hover:bg-red-700 rounded-xl px-4 py-1.5 flex items-center gap-1.5">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Breach Record
            </button>
          </div>
        </div>
      )}

      {/* Breach list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-primary-500" /></div>
      ) : breaches.length === 0 ? (
        <div className="card p-10 text-center">
          <CheckCircle2 size={32} className="mx-auto text-green-400 mb-3" />
          <p className="text-slate-500 text-sm">No data breaches recorded — great!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {breaches.map(b => {
            const deadlinePassed = b.assessment_deadline && new Date(b.assessment_deadline) < new Date() && b.status === 'assessing';
            return (
              <div key={b.id} className={`card border ${deadlinePassed ? 'border-red-300 bg-red-50/20' : 'border-slate-200'}`}>
                <div className="p-4 flex items-start gap-3">
                  <AlertTriangle size={18} className={`shrink-0 mt-0.5 ${deadlinePassed ? 'text-red-500' : 'text-yellow-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{b.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Discovered: {fmt(b.discovered_at)} · Assessment deadline: {fmt(b.assessment_deadline)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${statusColor[b.status] || statusColor.assessing}`}>
                          {b.status?.replace('_', ' ')}
                        </span>
                        <button onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                          {expanded === b.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                      </div>
                    </div>
                    {deadlinePassed && (
                      <p className="mt-1.5 text-xs font-semibold text-red-600">⚠ Assessment deadline passed — action required under NDB scheme</p>
                    )}
                  </div>
                </div>

                {expanded === b.id && (
                  <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
                    <p className="text-sm text-slate-700">{b.description}</p>
                    {b.data_types_affected?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {b.data_types_affected.map(t => (
                          <span key={t} className="text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><p className="text-slate-400">Individuals affected</p><p className="font-semibold">{b.individuals_affected || 0}</p></div>
                      <div><p className="text-slate-400">OAIC notified</p><p className={`font-semibold ${b.oaic_notified ? 'text-green-600' : 'text-red-500'}`}>{b.oaic_notified ? `Yes — ${fmtDate(b.oaic_notified_at)}` : 'No'}</p></div>
                      <div><p className="text-slate-400">Individuals notified</p><p className={`font-semibold ${b.individuals_notified ? 'text-green-600' : 'text-slate-600'}`}>{b.individuals_notified ? 'Yes' : 'No'}</p></div>
                      <div><p className="text-slate-400">Status</p>
                        <select value={b.status} onChange={e => updateStatus(b.id, { status: e.target.value })}
                          className="mt-0.5 text-xs border border-slate-200 rounded px-1.5 py-1 bg-white">
                          <option value="assessing">Assessing</option>
                          <option value="notifiable">Notifiable</option>
                          <option value="not_notifiable">Not notifiable</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {!b.oaic_notified && (
                        <button onClick={() => updateStatus(b.id, { oaic_notified: true, oaic_notified_at: new Date().toISOString() })}
                          className="btn-sm text-xs bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg px-3 py-1.5">
                          Mark OAIC Notified
                        </button>
                      )}
                      {!b.individuals_notified && (
                        <button onClick={() => updateStatus(b.id, { individuals_notified: true, individuals_notified_at: new Date().toISOString() })}
                          className="btn-sm text-xs bg-green-600 text-white hover:bg-green-700 rounded-lg px-3 py-1.5">
                          Mark Individuals Notified
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Retention Policies tab ────────────────────────────────────────────────────
function RetentionPoliciesTab() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/compliance/retention-policies')
      .then(r => setPolicies(r.data || []))
      .catch(() => toast.error('Failed to load retention policies'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 leading-relaxed">
        <strong>NDIS Practice Standards r.9(1)(f)</strong> — participant records must be retained for a minimum of{' '}
        <strong>7 years after the last service delivery</strong>. For participants who were minors, records must be
        kept until the person turns 25 (or 7 years, whichever is later). Records must be securely destroyed after
        the mandatory retention period.
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-primary-500" /></div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Entity Type</th>
                <th className="px-4 py-3 text-left">Retention Period</th>
                <th className="px-4 py-3 text-left">Legal Basis</th>
                <th className="px-4 py-3 text-left">Disposal Method</th>
                <th className="px-4 py-3 text-left">Review Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {policies.map(p => (
                <tr key={p.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-800 capitalize">{p.entity_type?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-800">{p.retention_years} years</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.legal_basis || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.disposal_method || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{p.review_date ? fmtDate(p.review_date) : '—'}</td>
                </tr>
              ))}
              {policies.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No retention policies configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ComplianceDashboard() {
  const location = useLocation();
  const qTab = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(qTab || 'breach');
  useEffect(() => { if (qTab) setTab(qTab); }, [qTab]);

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield size={22} className="text-indigo-600" /> Compliance Centre
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Privacy Act 1988 (Cth) · NDIS Practice Standards · NDB Scheme
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Lock,      color: '#6366f1', label: 'Encrypted at rest',   value: 'TFN · BSB · Account no.', sub: 'pgcrypto AES-256' },
          { icon: Eye,       color: '#10b981', label: 'Clinical access log',  value: 'All PHI access logged',   sub: 'Timestamped audit trail' },
          { icon: Database,  color: '#f59e0b', label: 'Data retention',       value: '7-year NDIS minimum',     sub: 'Auto-seeded policies' },
        ].map(({ icon: Icon, color, label, value, sub }) => (
          <div key={label} className="card p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + '18' }}>
              <Icon size={18} style={{ color }} />
            </div>
            <div>
              <p className="text-xs text-slate-500">{label}</p>
              <p className="font-semibold text-slate-800 text-sm">{value}</p>
              <p className="text-xs text-slate-400">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <Tab active={tab === 'breach'}    onClick={() => setTab('breach')}    icon={AlertTriangle} label="Breach Register" />
        <Tab active={tab === 'access'}    onClick={() => setTab('access')}    icon={Eye}           label="Access Log" />
        <Tab active={tab === 'retention'} onClick={() => setTab('retention')} icon={Archive}        label="Retention Policies" />
      </div>

      {/* Tab content */}
      <div>
        {tab === 'breach'    && <BreachRegisterTab />}
        {tab === 'access'    && <AccessLogTab />}
        {tab === 'retention' && <RetentionPoliciesTab />}
      </div>
    </div>
  );
}
